import React, { useCallback, useEffect, useRef, useState } from "react";
import { PROJECT_DETAILS, PROJECT_ORDER } from "./project-details";
import "./ProjectDetails.css";

function ProjectMedia({ item, className, style }) {
  if (item.video) {
    return (
      <video
        className={className}
        src={item.video}
        aria-label={item.alt}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        style={style}
      />
    );
  }

  return (
    <img
      className={className}
      src={item.image}
      alt={item.alt}
      loading="lazy"
      style={style}
    />
  );
}

// function getProjectByPath() {
//   const slug = window.location.pathname.split("/").filter(Boolean).pop();
//   const currentSlug = PROJECT_DETAILS[slug] ? slug : PROJECT_ORDER[0];
//   const currentIndex = PROJECT_ORDER.indexOf(currentSlug);
//   const prevSlug =
//     PROJECT_ORDER[(currentIndex - 1 + PROJECT_ORDER.length) % PROJECT_ORDER.length];
//   const nextSlug = PROJECT_ORDER[(currentIndex + 1) % PROJECT_ORDER.length];

//   return {
//     ...PROJECT_DETAILS[currentSlug],
//     slug: currentSlug,
//     prevProject: PROJECT_DETAILS[prevSlug].title,
//     prevProjectUrl: `/projects/${prevSlug}`,
//     nextProject: PROJECT_DETAILS[nextSlug].title,
//     nextProjectUrl: `/projects/${nextSlug}`,
//   };
// }

function getProjectByPath() {
  const slug = window.location.pathname.split("/").filter(Boolean).pop();
  const currentSlug = PROJECT_DETAILS[slug] ? slug : PROJECT_ORDER[0];
  const currentIndex = PROJECT_ORDER.indexOf(currentSlug);
  const prevSlug =
    PROJECT_ORDER[(currentIndex - 1 + PROJECT_ORDER.length) % PROJECT_ORDER.length];
  const nextSlug = PROJECT_ORDER[(currentIndex + 1) % PROJECT_ORDER.length];

  return {
    ...PROJECT_DETAILS[currentSlug],
    slug: currentSlug,
    prevProject: PROJECT_DETAILS[prevSlug].title,
    prevProjectUrl: `/${prevSlug}`,
    nextProject: PROJECT_DETAILS[nextSlug].title,
    nextProjectUrl: `/${nextSlug}`,
  };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function ProjectDetails({ project: providedProject }) {
  const project = providedProject || getProjectByPath();
  const {
    title,
    category,
    facts,
    chapters,
    gallery,
    heroImage,
    heroAlt,
    overview,
    fullbleedImage,
    fullbleedAlt,
    fullbleedQuote,

fullbleedImage2,
fullbleedAlt2,
fullbleedQuote2,

    prevProject,
    prevProjectUrl,
    nextProject,
    nextProjectUrl,
  } = project;

  const overviewRef = useRef(null);
  const galleryStripRef = useRef(null);
  const [overviewInView, setOverviewInView] = useState(false);

  useEffect(() => {
    const el = overviewRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setOverviewInView(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  const chapterRefs = useRef([]);
  const [activeChapter, setActiveChapter] = useState(0);

  useEffect(() => {
    const items = chapterRefs.current.filter(Boolean);
    if (!items.length) return;

    let animationFrame;

    const updateActiveChapter = () => {
      const viewportCenter = window.innerHeight / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.top + rect.height / 2;
        const distance = Math.abs(itemCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveChapter((currentIndex) =>
        currentIndex === closestIndex ? currentIndex : closestIndex
      );
      animationFrame = undefined;
    };

    const handleScroll = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(updateActiveChapter);
      }
    };

    updateActiveChapter();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [chapters.length]);

  const fullbleedRef = useRef(null);
  const [fullbleedInView, setFullbleedInView] = useState(false);

  useEffect(() => {
    const el = fullbleedRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setFullbleedInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25, rootMargin: "0px 0px -8% 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = useCallback((idx) => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const stepLightbox = useCallback(
    (dir) => {
      setLightboxIndex((current) => {
        if (current === null) return current;
        return (current + dir + gallery.length) % gallery.length;
      });
    },
    [gallery.length]
  );

  useEffect(() => {
    document.body.style.overflow = lightboxIndex !== null ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  useEffect(() => {
    if (lightboxIndex === null) return;

    function handleKeyDown(e) {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") stepLightbox(1);
      if (e.key === "ArrowLeft") stepLightbox(-1);
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, closeLightbox, stepLightbox]);

  const lightboxItem = lightboxIndex !== null ? gallery[lightboxIndex] : null;

  const scrollGallery = (direction) => {
    const strip = galleryStripRef.current;
    if (!strip) return;

    const item = strip.querySelector(".gallery-item");
    const gap = Number.parseFloat(window.getComputedStyle(strip).columnGap) || 0;
    strip.scrollBy({
      left: direction * ((item?.getBoundingClientRect().width || strip.clientWidth) + gap),
      behavior: "smooth",
    });
  };

  return (
    <div className="project-details">
      <div className="min-h-screen">
        <header className="band-dark hero min-h-screen">
          <img
            className="hero-img"
            src={heroImage}
            alt={heroAlt}
            width="1920"
            height="1280"
          />
          <div className="wrap hero-content">
            <p className="label text-muted-foreground">{category}</p>
            <h1 className="display d-xl mt-3">{title}</h1>
            <div className="facts-grid">
              {facts.map((fact) => (
                <div key={fact.label}>
                  <p className="label text-muted-foreground">{fact.label}</p>
                  <b className="display mt-1 block text-lg font-normal">
                    {fact.value}
                  </b>
                </div>
              ))}
            </div>
          </div>
        </header>

        <section className="wrap overview-section" ref={overviewRef}>
          <div className={`reveal${overviewInView ? " is-in" : ""}`}>
            <div className="overview-grid">
              <p className="label text-muted-foreground overview-label">
                Overview
              </p>
              <p className="display d-md overview-text">{overview}</p>
            </div>
          </div>
        </section>

        {fullbleedImage2 && (
  <section className="fullbleed-section">
    <img
      src={fullbleedImage2}
      alt={fullbleedAlt2}
      loading="lazy"
    />
    <div className="fullbleed-overlay">
      <p className="display d-md">{fullbleedQuote2}</p>
    </div>
  </section>
)}

        <section
          className="band-dark chapters-section"
          aria-label="Project story"
        >
          <div className="wrap chapters-grid">
            <div className="chapters-sticky-wrap">
              <div className="chapters-sticky frame">
                {/* {chapters.map((chapter, idx) => (
                  <ProjectMedia
                    key={chapter.alt}
                    item={chapter}
                    style={{
                      opacity: activeChapter === idx ? 1 : 0,
                      transform:
                        activeChapter === idx ? "scale(1)" : "scale(1.06)",
                    }}
                  />
                ))} */}

{chapters.map((chapter, idx) => (
  <ProjectMedia
    key={chapter.alt}
    item={chapter}
    className={chapter.mobileFit === "contain" ? "sticky-contain" : ""}
    style={{
      opacity: activeChapter === idx ? 1 : 0,
      transform:
        activeChapter === idx ? "scale(1)" : "scale(1.06)",
    }}
  />
))}

                <div className="chapters-dots">
                  {chapters.map((chapter, idx) => (
                    <span
                      key={chapter.alt}
                      style={{ opacity: activeChapter === idx ? 1 : 0.3 }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              {chapters.map((chapter, idx) => (
                <div
                  key={chapter.title}
                  className="chapter-item"
                  data-chapter-index={idx}
                  ref={(el) => (chapterRefs.current[idx] = el)}
                >
                  <p className="label text-muted-foreground">
                    {chapter.eyebrow}
                  </p>
                  <h2 className="display d-sm mt-5">{chapter.title}</h2>
                  <p className="body">{chapter.body}</p>
                  {/* <div className="frame chapter-mobile-img">
                    <ProjectMedia
                      className="frame-img"
                      item={chapter}
                    />
                  </div> */}

<div
  className={`frame chapter-mobile-img${
    chapter.mobileFit === "contain" ? " chapter-mobile-img--contain" : ""
  }`}
>
  <ProjectMedia
    className="frame-img"
    item={chapter}
  />
</div>

                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="fullbleed-section" ref={fullbleedRef}>
          <img
            src={fullbleedImage}
            alt={fullbleedAlt}
            loading="lazy"
            style={{ transform: fullbleedInView ? "scale(1)" : "scale(1.12)" }}
          />
          <div className="fullbleed-overlay">
            <p className="display d-md">{fullbleedQuote}</p>
          </div>
        </section>


        <section className="gallery-section" aria-label="Project gallery">
          <div className="wrap gallery-header">
            <h2 className="display d-md">Gallery</h2>
            <p className="label text-muted-foreground">
              Drag / click to enlarge
            </p>
          </div>

          <div className="gallery-strip" ref={galleryStripRef}>
            {gallery.map((item, idx) => (
              <button
                key={item.alt}
                className="gallery-item group"
                onClick={() => openLightbox(idx)}
              >
                <img src={item.image} alt={item.alt} loading="lazy" />
                <span className="gallery-caption">
                  <span className="text-sm">{item.caption}</span>
                  <span className="label">
                    {pad(idx + 1)} / {pad(gallery.length)}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {gallery.length > 1 && (
            <div className="gallery-controls" aria-label="Gallery navigation">
              <button
                type="button"
                className="gallery-control"
                onClick={() => scrollGallery(-1)}
                aria-label="Previous gallery image"
              >
                <span aria-hidden="true">‹</span>
              </button>
              <button
                type="button"
                className="gallery-control"
                onClick={() => scrollGallery(1)}
                aria-label="Next gallery image"
              >
                <span aria-hidden="true">›</span>
              </button>
            </div>
          )}
        </section>

        <nav className="wrap project-nav" aria-label="Project navigation">
          <a href={prevProjectUrl} className="group">
            <span className="label text-muted-foreground">Previous</span>
            <p className="display d-sm">{prevProject}</p>
          </a>
          <a href={nextProjectUrl} className="group text-right">
            <span className="label text-muted-foreground">Next</span>
            <p className="display d-sm">{nextProject}</p>
          </a>
        </nav>

        <div
          className={`lightbox band-dark${lightboxIndex !== null ? " open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Project image viewer"
        >
          <div className="lightbox-top">
            <p className="label text-muted-foreground">
              {lightboxIndex !== null
                ? `${pad(lightboxIndex + 1)} / ${pad(gallery.length)}`
                : ""}
            </p>
            <button className="label" onClick={closeLightbox}>
              Close
            </button>
          </div>
          <div className="lightbox-main">
            {lightboxItem && (
              <img src={lightboxItem.image} alt={lightboxItem.alt} />
            )}
            <button
              className="lightbox-nav-btn prev"
              aria-label="Previous image"
              onClick={() => stepLightbox(-1)}
            />
            <button
              className="lightbox-nav-btn next"
              aria-label="Next image"
              onClick={() => stepLightbox(1)}
            />
          </div>
          <div className="lightbox-bottom">
            <p>{lightboxItem ? lightboxItem.caption : ""}</p>
            <div className="lightbox-bottom-nav" aria-label="Gallery image navigation">
              <button
                type="button"
                aria-label="Previous image"
                onClick={() => stepLightbox(-1)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M15 5L8 12L15 19"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={() => stepLightbox(1)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M9 5L16 12L9 19"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
